
import { useState, useRef, useEffect, useMemo } from 'react';

interface CityAutocompleteProps {
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onCommit?: (value: string) => void;
  suggestions: string[];
  placeholder: string;
  icon: string;
  required?: boolean;
  allowCustomValue?: boolean;
  customValueLabel?: (value: string) => string;
}

function normalizeSearchValue(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function rankSuggestion(suggestion: string, query: string) {
  const normalized = normalizeSearchValue(suggestion);

  if (normalized === query) return 0;
  if (normalized.startsWith(query)) return 1;
  if (normalized.split(/[\s,/-]+/).some((part) => part.startsWith(query))) return 2;
  if (normalized.includes(query)) return 3;

  return null;
}

export default function CityAutocomplete({
  id,
  name,
  value,
  onChange,
  onCommit,
  suggestions,
  placeholder,
  icon,
  required = false,
  allowCustomValue = false,
  customValueLabel,
}: CityAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const normalizedValue = normalizeSearchValue(value);
  const filtered = useMemo(() => {
    if (!normalizedValue) return suggestions.slice(0, 8);

    return suggestions
      .map((suggestion, index) => ({
        suggestion,
        index,
        rank: rankSuggestion(suggestion, normalizedValue),
      }))
      .filter((item): item is { suggestion: string; index: number; rank: number } => item.rank !== null)
      .sort((left, right) => left.rank - right.rank || left.index - right.index)
      .slice(0, 8)
      .map((item) => item.suggestion);
  }, [normalizedValue, suggestions]);
  const hasExactSuggestionMatch = filtered.some((city) => {
    const normalized = normalizeSearchValue(city);
    return normalized === normalizedValue;
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (val: string) => {
    onChange(val);
    if (val.trim().length >= 1) {
      const hasMatches = suggestions.some((suggestion) =>
        rankSuggestion(suggestion, normalizeSearchValue(val)) !== null,
      );
      setIsOpen(hasMatches || allowCustomValue);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (city: string) => {
    onChange(city);
    onCommit?.(city);
    setIsOpen(false);
  };

  const handleCommitCustomValue = () => {
    const trimmedValue = value.trim();
    if (!allowCustomValue || !trimmedValue) return;
    onCommit?.(trimmedValue);
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (value.trim().length >= 1 && (filtered.length > 0 || allowCustomValue)) {
      setIsOpen(true);
    } else if (value.trim().length === 0) {
      setIsOpen(true);
    } else if (allowCustomValue) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <i className={`${icon} absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A84C] z-10`}></i>
      <input
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && allowCustomValue && value.trim()) {
            e.preventDefault();
            handleCommitCustomValue();
          }
        }}
        required={required}
        autoComplete="off"
        className="w-full pl-11 pr-5 py-3.5 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl focus:ring-2 focus:ring-[#C9A84C] focus:border-transparent outline-none transition-all"
        placeholder={placeholder}
      />
      {isOpen && (filtered.length > 0 || (allowCustomValue && value.trim().length > 0)) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A2A44] border border-white/20 rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto">
          {filtered.map((city, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(city)}
              className="w-full text-left px-4 py-3 text-sm text-white/90 hover:bg-[#C9A84C]/20 hover:text-[#C9A84C] transition-colors cursor-pointer flex items-center gap-3 first:rounded-t-xl last:rounded-b-xl"
            >
              <i className="ri-map-pin-2-line text-[#C9A84C]/60 text-xs"></i>
              <span>{city}</span>
            </button>
          ))}
          {allowCustomValue && value.trim().length > 0 && !hasExactSuggestionMatch && (
            <button
              type="button"
              onClick={handleCommitCustomValue}
              className="w-full text-left px-4 py-3 text-sm text-white hover:bg-[#C9A84C]/20 hover:text-[#C9A84C] transition-colors cursor-pointer flex items-center gap-3 border-t border-white/10"
            >
              <i className="ri-add-line text-[#C9A84C]/80 text-xs"></i>
              <span>{customValueLabel ? customValueLabel(value.trim()) : value.trim()}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
